import { useAutoneTranslation } from '@autone/translations';
import { getLocale } from '@autone/utils';
import type * as PopoverPrimitive from '@radix-ui/react-popover';
import { format as baseFormat } from 'date-fns';
import * as React from 'react';
import { type Matcher } from 'react-day-picker';

import { type Language } from '../components/sidebar/language-selector/LanguageSelector.types';
import { CalendarIcon } from '../icons';
import { cn } from '../utils';

import { Button } from './Button';
import { DatePickerCalendar } from './DatePickerCalendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

export type DisabledDays = Matcher;

const formatMap = {
  en: 'dd/MM/yyyy',
  'en-US': 'MM/dd/yyyy',
  fr: 'dd/MM/yyyy',
  it: 'dd/MM/yyyy',
} as const;

export const formatDatePickerDate = (date: Date, lang: string) => {
  const locale = getLocale(lang);
  const pattern = formatMap?.[lang as keyof typeof formatMap] || formatMap.en;
  return baseFormat(date, pattern, { locale });
};

export interface SingleDatePickerProps extends PopoverPrimitive.PopoverProps {
  date?: Date;
  placeholderText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (...event: any[]) => void;
  className?: string;
  inputClassName?: string;
  disabledInput?: boolean;
  disabledDays?: DisabledDays;
  defaultMonth?: Date;
  id: string;
}

const DatePicker = React.forwardRef<HTMLButtonElement, SingleDatePickerProps>(
  (
    {
      id,
      date,
      placeholderText,
      onChange,
      className,
      inputClassName,
      disabledInput,
      disabledDays,
      defaultMonth,
      ...props
    },
    ref,
  ) => {
    const { lang } = useAutoneTranslation();

    return (
      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            aria-label="Date picker trigger"
            ref={ref}
            data-testid={id}
            variant="outline"
            disabled={disabledInput}
            className={cn(
              'w-full justify-between text-left font-normal text-sm h-[52px]',
              !date && 'text-muted-foreground',
              inputClassName,
            )}
          >
            {date
              ? formatDatePickerDate(date, lang as Language)
              : placeholderText}
            <CalendarIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <DatePickerCalendar
            data-testid={`${id}-calendar`}
            mode="single"
            selected={date}
            onSelect={onChange}
            showOutsideDays={true}
            className={className}
            disabled={disabledDays}
            defaultMonth={defaultMonth}
          />
        </PopoverContent>
      </Popover>
    );
  },
);
DatePicker.displayName = 'DatePicker';

export { DatePicker };
